import { Auth0Client } from '@auth0/auth0-spa-js';
import { delay } from '@utils/common';
import dayjs from 'dayjs';
import { CONFIG } from '../config';

const auth0 = new Auth0Client({
  domain: CONFIG.auth.domain ?? '',
  clientId: CONFIG.auth.clientId ?? '',
  authorizationParams: {
    scope: 'client_credentials',
    audience: 'onevest',
  },
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
  useCookiesForTransactions: true,
  useRefreshTokensFallback: true,
});

export const JWT_CLAIMS_NAMESPACE = 'https://onevest.com';

export const getOrganizationKeyFromURL = (organizationName?: string) => {
  let organizationKey: string | null = null;
  let auth0RedirectUri = CONFIG.app.basePath || '';
  if (
    typeof window !== 'undefined' &&
    ['staging', 'production', 'casandbox', 'ussandbox'].includes(
      CONFIG.app.env ?? '',
    )
  ) {
    const { host, origin } = window.location;
    const key = host.split('.')?.[0];
    if (key && !['dev-onevest-app', 'onevest-app'].includes(key)) {
      organizationKey = key;
      auth0RedirectUri = origin;
    } else if (key && organizationName && CONFIG.app.orgBasePath) {
      organizationKey = organizationName;
      auth0RedirectUri = CONFIG.app.orgBasePath.replace(
        'organization',
        organizationName,
      );
    }

    console.log({
      event: 'ORGANIZATION_AUTH',
      organizationKey,
      auth0RedirectUri,
      url: origin,
    });
  }
  return { organizationKey, auth0RedirectUri };
};

export const auth0Logout = async ({
  redirectUrl,
  federated,
}: {
  redirectUrl?: string;
  federated?: boolean;
} = {}) => {
  const isAuthenticatedWithAuth0 = await auth0.isAuthenticated();
  if (isAuthenticatedWithAuth0) {
    const { auth0RedirectUri } = getOrganizationKeyFromURL();
    await auth0.logout({
      logoutParams: {
        returnTo: redirectUrl ?? auth0RedirectUri,
        ...(federated ? { federated: true } : {}),
      },
    });
    await delay(500); // Add 500ms delay to handle external redirect as [setResetKey in ovApolloProvider] resets app state and causes a race condition
  }
};

export const auth0RefreshToken = async ({
  redirectToPath,
  orgSubdomain,
}: {
  redirectToPath?: string;
  orgSubdomain?: string;
} = {}) => {
  const locationPath = redirectToPath ?? window.location.pathname;
  const shouldRedirectToPath = locationPath?.trim() !== '/';
  if (shouldRedirectToPath) {
    const auth0RedirectPath = {
      locationPath,
      expireAt: dayjs().add(5, 'minutes').toDate(),
    };
    localStorage.setItem(
      'auth0RedirectPath',
      JSON.stringify(auth0RedirectPath),
    );
  }
  const { auth0RedirectUri, organizationKey } = getOrganizationKeyFromURL();

  const orgKey = orgSubdomain ?? organizationKey;

  await auth0.loginWithRedirect({
    authorizationParams: {
      redirect_uri: auth0RedirectUri,
      ...(orgKey ? { organization: orgKey } : {}),
    },
  });
};

export const auth0RefreshTokenSilently = async ({
  redirectToPath,
  orgSubdomain,
}: {
  redirectToPath?: string;
  orgSubdomain?: string;
} = {}): Promise<string> => {
  let token = '';
  const { organizationKey } = getOrganizationKeyFromURL();

  const orgKey = orgSubdomain ?? organizationKey;

  await auth0
    .getTokenSilently({
      cacheMode: 'off',
      authorizationParams: {
        ...(orgKey ? { organization: orgKey } : {}),
      },
    })
    .then(async (newToken: string) => {
      token = newToken;
    })
    .catch(async (e: any) => {
      auth0RefreshToken({ redirectToPath, orgSubdomain });
      throw new Error(e);
    });

  return token;
};

export default auth0;
